import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Plus, Edit,
  BookOpen, X, AlertCircle, Save, Trash2, BookMarked, GripVertical,
  Download, Eye, ArrowUpDown, ArrowUp, ArrowDown,
  Loader2, ChevronDown
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { API_BASE_URL } from '../../../api/config';
import trainingService from '../../../api/services/trainingService';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import Button from '../../../components/common/Button/Button';
import { formatDate } from '../../../utils/stringUtils';
import toast from 'react-hot-toast';
import ExportModal from '../../../components/common/ExportModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { learningPathCategoryService, courseTypeService } from '../../../api/crm/config/configService';

const LearningPaths = () => {
  const navigate = useNavigate();


  // State management
  const [learningPaths, setLearningPaths] = useState([]);
  const [courseCategories, setCourseCategories] = useState([]);
  const [pathCategories, setPathCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Handle input blur
  const handleBlur = (e) => {
    const { name } = e.target;
    const field = e.target.getAttribute('data-field') || name;
    setTouched((prev) => ({ ...prev, [field]: true }));

    // Validate the field immediately on blur
    const errors = validatePathForm(pathForm);
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (errors[field]) {
        newErrors[field] = errors[field];
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const isFirstRender = useRef(true);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    category: searchParams.get('category') || 'all',
    status: searchParams.get('status') || 'all'
  });
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('startDate') || '');
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('endDate') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || null);
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || null);
  const [showPathDetails, setShowPathDetails] = useState(false);
  const [selectedPathForView, setSelectedPathForView] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [debouncedCourseSearchTerm, setDebouncedCourseSearchTerm] = useState('');
  const [courseSearchTimeout, setCourseSearchTimeout] = useState(null);
  const [courseCategoryFilter, setCourseCategoryFilter] = useState('all');
  const [pathDetailsLoading, setPathDetailsLoading] = useState(false);
  const [pathDetailsError, setPathDetailsError] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, pathId: null, pathTitle: '' });
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);
  const [pagination, setPagination] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Map());
  const selectedCount = selectedRows.size;
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);

  const baseExportFilters = useMemo(() => ({
    search: searchTerm,
    category: selectedFilters.category,
    status: selectedFilters.status,
    level: selectedFilters.level,
    difficulty: selectedFilters.difficulty,
    startDate: filterStartDate,
    endDate: filterEndDate,
  }), [searchTerm, selectedFilters, filterStartDate, filterEndDate]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filters = overrideFilters ?? baseExportFilters;
      const payload = {
        module: "learning-paths",
        scope,
        format,
        filters,
        page: overridePage ?? currentPage,
        limit: overrideLimit ?? itemsPerPage,
        sortBy: "createdAt",
        sortOrder: "desc",
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Export failed");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `learning-paths-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, currentPage, itemsPerPage]
  );

  // Bulk Delete State
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // API Data State - initialize as null to prevent flashing
  const [availableCourses, setAvailableCourses] = useState([]);

  // Utility: capitalize first non-space character
  const capitalizeFirstLetter = (text) => {
    if (!text || typeof text !== 'string') return '';
    const match = text.match(/^(\s*)(.)/);
    if (!match) return text;
    const [, leading, first] = match;
    return leading + first.toUpperCase() + text.slice(leading.length + 1);
  };

  // Format duration from minutes to hours/minutes/seconds
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) {
      return '0s';
    }
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    // Show seconds if there are any, or if there are no hours/minutes
    if (secs > 0) parts.push(`${secs}s`);

    return parts.length > 0 ? parts.join(' ') : '0s';
  };

  // Format duration from hours to readable format
  const formatDurationFromHours = (hours) => {
    if (!hours || hours === 0) {
      return '0h';
    }
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);

    return parts.length > 0 ? parts.join(' ') : '0h';
  };

  const formatDescriptionText = (text) => {
    if (!text || typeof text !== 'string') return 'No description provided';
    const cleaned = text.trim();
    if (cleaned.length === 0) return 'No description provided';
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  // Derive duration in minutes for a path from available data
  const getPathDurationInMinutes = (path) => {
    if (!path) return 0;

    const { totalCourseDurationMinutes, courses, estimatedDuration } = path;

    if (totalCourseDurationMinutes !== undefined && totalCourseDurationMinutes !== null) {
      const minutes = Number(totalCourseDurationMinutes);
      if (!Number.isNaN(minutes)) {
        return minutes;
      }
    }

    if (Array.isArray(courses) && courses.length > 0) {
      return courses.reduce((total, course) => {
        const duration =
          Number(course?.duration) ||
          Number(course?.course?.duration) ||
          0;
        if (Number.isNaN(duration)) {
          return total;
        }
        return total + duration;
      }, 0);
    }

    const estimatedHours = Number(estimatedDuration);
    if (!Number.isNaN(estimatedHours)) {
      return estimatedHours * 60;
    }

    return 0;
  };

  // Learning path form state
  const [pathForm, setPathForm] = useState({
    title: '',
    description: '',
    shortDescription: '',
    category: '',
    subcategory: '',
    estimatedDuration: '',
    prerequisites: [],
    courses: [],
    milestones: [],
    learningObjectives: [''],
    skills: [''],
    tags: [],
    coverImage: null,
    enrollment: {
      maxStudents: 100,
      autoEnrollment: false,
      enrollmentRules: {}
    },
    assessment: {
      passingScore: 70,
      maxAttempts: 3,
      timeLimit: 120
    },
    certificate: {
      enabled: true,
      template: 'default',
      validityPeriod: 24,
      requirements: {
        completionPercentage: 100,
        passingScore: 70
      }
    },
    pricing: {
      type: 'free',
      amount: 0,
      currency: 'USD'
    },
    settings: {
      allowSelfEnrollment: true,
      requireApproval: false,
      allowPause: true,
      maxPauseDays: 30,
      allowRetake: true,
      retakeCooldown: 7
    },
    status: 'draft',
    completionCriteria: 'All Required Courses',
    enrollmentDeadline: ''
  });

  // Fetch categories
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch Course Categories (for selection modal)
        const courseRes = await courseTypeService.getAll({ activeOnly: true });
        if (courseRes.success) {
          setCourseCategories(courseRes.data || []);
        }

        // Fetch Learning Path Categories (for create/edit form)
        const pathRes = await learningPathCategoryService.getAll({ isActive: true });
        if (pathRes.success) {
          setPathCategories(pathRes.data || []);
        }
      } catch (err) {
      }
    };

    fetchDropdownData();
  }, []);

  // Validation functions
  const validateSearchTerm = (term) => {
    if (typeof term !== 'string') {
      return 'Search term must be a string';
    }
    if (term.length > 100) {
      return 'Search term must be less than 100 characters';
    }
    return null;
  };

  // Helper function for text input key press (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    const char = e.key;
    if (!/^[a-zA-Z0-9\s,]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {
      e.preventDefault();
    }
  };

  // Helper function for number input key down (positive numbers only, 0 and greater)
  const handleNumberKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period (for decimal)
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  const validateFilters = (filters) => {
    const errors = {};


    // Validate category filter
    const validCategories = ['all', ...pathCategories.map(c => c.name.toLowerCase())];
    if (filters.category && !validCategories.includes(filters.category.toLowerCase())) {
      errors.category = 'Invalid category selected';
    }


    // Validate status filter
    const validStatuses = ['all', 'draft', 'published', 'archived'];
    if (filters.status && !validStatuses.includes(filters.status)) {
      errors.status = 'Invalid status selected';
    }

    // Validate date range
    if (filterStartDate && filterEndDate && filterEndDate < filterStartDate) {
      errors.dateRange = 'End date cannot be before start date';
    }

    return errors;
  };

  const totalPaths = pagination?.totalPaths ?? pagination?.total ?? pagination?.totalItems ?? (learningPaths?.length || 0);
  const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
  const currentPageNum = pagination?.currentPage || currentPage || 1;
  const totalPages = pagination?.totalPages || Math.max(1, Math.ceil((totalPaths || 0) / (effectivePaginationLimit || 1)));
  const paginationRangeStart = totalPaths === 0 ? 0 : ((currentPageNum - 1) * effectivePaginationLimit) + 1;
  const paginationRangeEnd = Math.min(currentPageNum * effectivePaginationLimit, totalPaths);
  const hasPrevPage = pagination?.hasPrevPage ?? currentPageNum > 1;
  const hasNextPage = pagination?.hasNextPage ?? currentPageNum < totalPages;
  const shouldShowPagination = !loading && totalPages > 1 && totalPaths > effectivePaginationLimit;

  // Load learning paths on component mount and when filters change
  useEffect(() => {
    loadLearningPaths();
  }, [selectedFilters, filterStartDate, filterEndDate, currentPage, itemsPerPage]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (courseSearchTimeout) {
        clearTimeout(courseSearchTimeout);
      }
    };
  }, [searchTimeout, courseSearchTimeout]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'category', 'status', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (selectedFilters.category && selectedFilters.category !== 'all') params.set('category', selectedFilters.category);
      if (selectedFilters.status && selectedFilters.status !== 'all') params.set('status', selectedFilters.status);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortBy) params.set('sortBy', sortBy);
      if (sortOrder) params.set('sortOrder', sortOrder);

      return params;
    }, { replace: true });

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [
    searchTerm,
    selectedFilters.category,
    selectedFilters.status,
    filterStartDate,
    filterEndDate,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    setSearchParams
  ]);

  // Load learning paths from API with comprehensive error handling
  const loadLearningPaths = async () => {
    // Validate search term
    const searchError = validateSearchTerm(searchTerm);
    if (searchError) {
      setError(searchError);
      return;
    }

    // Validate filters
    const filterErrors = validateFilters(selectedFilters);
    if (Object.keys(filterErrors).length > 0) {
      setValidationErrors(filterErrors);
      setError('Please fix filter validation errors');
      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      const queryParams = {
        search: searchTerm || '',
        category: selectedFilters.category || 'all',
        status: selectedFilters.status || 'all',
        startDate: filterStartDate,
        endDate: filterEndDate,
        page: currentPage || 1,
        limit: itemsPerPage || 10
      };

      const result = await trainingService.getLearningPaths(queryParams);

      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response format from learning paths API');
      }

      if (result.success) {
        const paths = result.data?.learningPaths || [];
        const paginationData = result.data?.pagination || {};

        // Validate that paths is an array
        if (Array.isArray(paths)) {
          setLearningPaths(paths);
        } else {
          setLearningPaths([]);
        }

        // Validate pagination object
        if (typeof paginationData === 'object' && paginationData !== null) {
          setPagination(paginationData);
          if (paginationData.currentPage && paginationData.currentPage !== currentPage) {
            setCurrentPage(paginationData.currentPage);
          }
        } else {
          setPagination({});
        }
      } else {
        const errorMessage = result.error || 'Failed to load learning paths';
        setError(errorMessage);
        setLearningPaths([]);
        setPagination({});
      }
    } catch (error) {
      setError('Failed to load learning paths. Please try again.');
      setLearningPaths([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCourses = async () => {
    try {
      setLoading(true);
      const result = await trainingService.getCourses({
        page: 1,
        limit: 100,
        status: 'published',
        search: debouncedCourseSearchTerm,
        type: courseCategoryFilter !== 'all' ? courseCategoryFilter : undefined
      });

      if (result.success) {
        setAvailableCourses(result.data.courses || []);
      }
    } catch (err) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  // Trigger course fetch when modal filters change
  useEffect(() => {
    if (showCourseModal) {
      loadAvailableCourses();
    }
  }, [debouncedCourseSearchTerm, courseCategoryFilter, showCourseModal]);

  // Handle search with debounce
  const handleSearch = (e) => {
    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
    const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);

    // Validate search term
    const searchError = validateSearchTerm(value);
    if (searchError) {
      setValidationErrors(prev => ({ ...prev, search: searchError }));
      return;
    }

    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
    setError(null);
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.search;
      return newErrors;
    });

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      loadLearningPaths();
    }, 400);
    setSearchTimeout(timeout);
  };

  // Helper function for search input key press
  const handleSearchKeyPress = (e) => {
    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
    if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
    setError(null);
  };

  // Handle date changes
  const handleDateChange = ({ startDate, endDate }) => {
    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
    setCurrentPage(1);
    setError(null);
  };

  // Handle sort changes
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
    setError(null);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setSortOrder('asc');
    setCurrentPage(1);
    setError(null);
  };

  // Clear all filters and search
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedFilters({
      category: 'all',
      status: 'all'
    });
    setFilterStartDate('');
    setFilterEndDate('');
    setCurrentPage(1);
    setItemsPerPage(10);
    setError(null);
    setValidationErrors({});
  };

  const handlePageChange = (page) => {
    if (!page || page === currentPageNum || page < 1) return;
    const maxPages = totalPages || 1;
    if (page > maxPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    if (!limit || limit === itemsPerPage) return;
    setItemsPerPage(limit);
    setCurrentPage(1);
    setSelectedRows(new Map());
  };

  // Row selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Map(selectedRows);
      learningPaths?.forEach(path => {
        newSelected.set(path.id, path);
      });
      setSelectedRows(newSelected);
    } else {
      // Global reset when unchecking header checkbox
      setSelectedRows(new Map());
    }
  };

  const handleSelectRow = (path) => {
    const newSelected = new Map(selectedRows);
    if (newSelected.has(path.id)) {
      newSelected.delete(path.id);
    } else {
      newSelected.set(path.id, path);
    }
    setSelectedRows(newSelected);
  };

  // --- Bulk Delete Handlers ---
  const handleBulkDelete = () => {
    if (selectedRows.size === 0) {
      toast.error('Please select at least one learning path to delete');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    const ids = Array.from(selectedRows.keys());
    let successCount = 0;
    let errorCount = 0;

    for (const id of ids) {
      try {
        const result = await trainingService.deleteLearningPath(id);
        if (result && result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        errorCount++;
      }
    }

    setIsBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedRows(new Map());
    await loadLearningPaths();

    if (successCount > 0) toast.success(`${successCount} learning path(s) deleted successfully`);
    if (errorCount > 0) toast.error(`Failed to delete ${errorCount} learning path(s)`);
  };

  const mapLearningPathsForExport = (paths) => {
    return paths.map(path => ({
      'Learning Path Name': path.title || '',
      'Description': path.description || '',
      'Total Courses': path.totalCourses || path.courses?.length || 0,
      'Course Sequence': path.courses?.map(c => c.course?.title || c.title || 'N/A').join(' -> ') || 'N/A',
      'Prerequisite Courses': path.prerequisites?.map(p => p.title || 'N/A').join(', ') || 'N/A',
      'Status': capitalizeValue(path.status || ''),
      'Created By': path.createdBy ? `${path.createdBy.firstName || ''} ${path.createdBy.lastName || ''}`.trim() || 'N/A' : 'N/A',
      'Created Date': path.createdAt ? formatDate(path.createdAt, { includeTime: true }) : 'N/A',
      'Last Updated Date': path.updatedAt ? formatDate(path.updatedAt, { includeTime: true }) : 'N/A',
      'Last Updated By': path.updatedBy ? `${path.updatedBy.firstName || ''} ${path.updatedBy.lastName || ''}`.trim() || 'N/A' : 'N/A'
    }));
  };

  const handleExportClick = () => {
    if (selectedCount > 0) {
      setShowInlineSelectedFormats(!showInlineSelectedFormats);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportSelected = async (format) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    const ids = Array.from(selectedRows.keys());
    if (!ids.length) {
      toast.error('No selected paths found to export.');
      return;
    }
    setExportLoading(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
    try {
      await requestBackendExport({
        scope: 'selected',
        format,
        selectedIds: ids,
      });
      toast.success(`${ids.length} learning path(s) exported!`, { id: toastId });
      setShowInlineSelectedFormats(false);
    } catch (error) {
      toast.error(error?.message || 'Export failed.', { id: toastId });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportScoped = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        if (scope === 'currentPage' && (!learningPaths || !learningPaths.length)) {
          toast.error('No learning paths on current page to export.', { id: toastId });
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.', { id: toastId });
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, createdAtStart: startDate, createdAtEnd: endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          toast.dismiss(toastId);
          return;
        }

        toast.success('Export completed successfully', { id: toastId });
        setShowExportModal(false);
      } catch (error) {
          toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [learningPaths, baseExportFilters, requestBackendExport]
  );

  // Reset form to initial state
  const resetForm = () => {
    setPathForm({
      title: '',
      description: '',
      shortDescription: '',
      category: '',
      subcategory: '',
      estimatedDuration: '',
      prerequisites: [],
      courses: [],
      milestones: [],
      learningObjectives: [''],
      skills: [''],
      tags: [],
      coverImage: null,
      enrollment: {
        maxStudents: 100,
        autoEnrollment: false,
        enrollmentRules: {}
      },
      assessment: {
        passingScore: 70,
        maxAttempts: 3,
        timeLimit: 120
      },
      certificate: {
        enabled: true,
        template: 'default',
        validityPeriod: 24,
        requirements: {
          completionPercentage: 100,
          passingScore: 70
        }
      },
      pricing: {
        type: 'free',
        amount: 0,
        currency: 'USD'
      },
      settings: {
        isActive: true,
        isPublic: false,
        allowRetakes: true,
        showProgress: true,
        enableNotifications: true
      },
      status: 'draft',
      completionCriteria: 'All Required Courses',
      enrollmentDeadline: ''
    });
    setValidationErrors({});
    setTouched({});
    setError(null);
  };

  // Handle course search
  // Handle course search with debounce
  const handleCourseSearch = (e) => {
    // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
    const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
    setCourseSearchTerm(value);

    if (courseSearchTimeout) {
      clearTimeout(courseSearchTimeout);
    }

    const timeout = setTimeout(() => {
      setDebouncedCourseSearchTerm(value);
    }, 400);
    setCourseSearchTimeout(timeout);
  };

  // Handle course category filter
  const handleCourseCategoryFilter = (e) => {
    setCourseCategoryFilter(e.target.value);
  };

  // Filter courses based on search and category
  const getFilteredCourses = () => {
    if (!availableCourses || availableCourses.length === 0) return [];

    // Server-side already filtered, return availableCourses directly
    return availableCourses;
  };

  const sortedPaths = useMemo(() => {
    if (!learningPaths) return [];
    if (!sortBy) return learningPaths;

    return [...learningPaths].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'description':
          aValue = (a.description || a.shortDescription || '').toLowerCase();
          bValue = (b.description || b.shortDescription || '').toLowerCase();
          break;
        case 'category':
          aValue = (a.category || '').toLowerCase();
          bValue = (b.category || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'courses':
          aValue = (a.courses?.length || a.totalCourses || 0);
          bValue = (b.courses?.length || b.totalCourses || 0);
          break;
        case 'duration':
        case 'estimatedDuration':
          aValue = getPathDurationInMinutes(a);
          bValue = getPathDurationInMinutes(b);
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [learningPaths, sortBy, sortOrder]);


  // Validation functions for learning path form - matching TrainingCatalog style
  const validatePathForm = (form) => {
    const errors = {};

    // Validate title (Required)
    if (!form.title || typeof form.title !== 'string' || form.title.trim().length === 0) {
      errors.title = 'Learning path title is required and cannot be empty';
    } else if (form.title.trim().length < 3) {
      errors.title = 'Learning path title must be at least 3 characters long';
    } else if (form.title.trim().length > 100) {
      errors.title = 'Learning path title cannot exceed 100 characters';
    } else if (!/^[a-zA-Z0-9\s\-_&().,]+$/.test(form.title.trim())) {
      errors.title = 'Learning path title contains invalid characters. Only letters, numbers, spaces, and basic punctuation are allowed';
    } else if (/^[a-z]/.test(form.title.trim())) {
      errors.title = 'First letter of the path title must be capital';
    }

    // Validate description (Required)
    if (!form.description || typeof form.description !== 'string' || form.description.trim().length === 0) {
      errors.description = 'Learning path description is required and cannot be empty';
    } else if (form.description.trim().length < 10) {
      errors.description = 'Learning path description must be at least 10 characters long';
    } else if (form.description.trim().length > 500) {
      errors.description = 'Learning path description cannot exceed 500 characters';
    }

    // Validate category (Required)
    const categoryNames = pathCategories.map(c => (c.name || '').toLowerCase());
    if (!form.category) {
      errors.category = 'Please select a category';
    } else if (pathCategories.length > 0 && !categoryNames.includes(form.category.toLowerCase())) {
      errors.category = 'Please select a valid category';
    }

    // Validate courses (Required)
    if (!form.courses || form.courses.length === 0) {
      errors.courses = 'At least one course must be selected';
    }

    return errors;
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setPathForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle nested object changes
  const handleNestedChange = (parentField, childField, value) => {
    setPathForm(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));

    // Clear validation error for this field
    const errorKey = `${parentField}_${childField}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  };

  // Handle array field changes
  const handleArrayChange = (field, index, value) => {
    setPathForm(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  // Add new item to array
  const addArrayItem = (field, defaultValue = '') => {
    setPathForm(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  // Remove item from array
  const removeArrayItem = (field, index) => {
    setPathForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Handle create learning path
  const handleCreatePath = async (e) => {
    // Prevent default form submission if called from form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }


    const formErrors = validatePathForm(pathForm);

    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);
      setTouched({
        title: true,
        description: true,
        category: true,
        courses: true
      });
      setError(null); // Clear general error, show field-specific errors

      // Auto-scroll to first validation error
      setTimeout(() => {
        const firstErrorField = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }, 100);

      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Generate unique pathId
      const pathId = `LP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Calculate estimatedDuration from courses (in hours)
      const coursesForPayload = pathForm.courses?.map(course => ({
        course: course.id,
        order: pathForm.courses.indexOf(course) + 1,
        isRequired: course.isRequired || true
      })) || [];

      // Calculate total duration from courses in minutes, then convert to hours
      const totalMinutes = pathForm.courses?.reduce((total, c) => total + (Number(c.duration) || 0), 0) || 0;
      const calculatedEstimatedDuration = Math.round(totalMinutes / 60) || 0;

      // Prepare the learning path data
      const learningPathData = {
        pathId: pathId,
        title: pathForm.title,
        description: pathForm.description,
        category: pathForm.category,
        estimatedDuration: calculatedEstimatedDuration,
        status: pathForm.status || 'draft',
        completionCriteria: pathForm.completionCriteria,
        enrollment: {
          maxStudents: pathForm.enrollment?.maxStudents || 100,
          autoEnrollment: pathForm.enrollment?.autoEnrollment || false,
          enrollmentDeadline: pathForm.enrollmentDeadline
        },
        courses: coursesForPayload,
        learningObjectives: pathForm.learningObjectives?.filter(obj => obj.trim()) || [],
        skills: pathForm.skills?.filter(skill => skill.trim()) || [],
        tags: pathForm.tags || []
      };

      const result = await trainingService.createLearningPath(learningPathData);

      if (result.success) {
        const created = result.data || {};
        setLearningPaths(prev => Array.isArray(prev) ? [created, ...prev] : [created]);
        setCurrentPage(1);
        setShowCreateModal(false);
        resetForm();
        setError(null);
      } else {
        setError(result.error || 'Failed to create learning path');
      }
    } catch (error) {
      setError('Failed to create learning path. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit learning path
  const handleEditPath = async (path) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch complete learning path details from backend
      const result = await trainingService.getLearningPathById(path.id);
      if (result.success) {
        const pathData = result.data;
        setSelectedPath(pathData);

        // Transform existing courses to match form structure
        const existingCourses = pathData.courses?.map((course, index) => {
          // Handle populated course data structure
          const courseData = course.course || course;
          return {
            id: courseData._id || courseData.id || course.course,
            title: courseData.title || course.title || course.courseTitle,
            level: courseData.level || course.level || 'beginner',
            duration: courseData.duration || course.duration || 60,
            category: courseData.category || course.category || 'professional',
            isRequired: course.isRequired !== undefined ? course.isRequired : true
          };
        }) || [];

        // Format date for input field
        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
          } catch (error) {
            return '';
          }
        };

        setPathForm({
          title: pathData.title || '',
          description: pathData.description || '',
          shortDescription: pathData.shortDescription || '',
          category: pathData.category || '',
          subcategory: pathData.subcategory || '',
          estimatedDuration: pathData.estimatedDuration || '',
          prerequisites: pathData.prerequisites || [],
          courses: existingCourses,
          milestones: pathData.milestones || [],
          learningObjectives: pathData.learningObjectives || [''],
          skills: pathData.skills || [''],
          tags: pathData.tags || [],
          coverImage: pathData.coverImage || null,
          status: pathData.status || 'draft',
          completionCriteria: pathData.completionCriteria || 'All Required Courses',
          enrollmentDeadline: formatDateForInput(pathData.enrollment?.enrollmentDeadline),
          enrollment: pathData.enrollment || {
            maxStudents: 100,
            autoEnrollment: false,
            enrollmentRules: {}
          },
          assessment: pathData.assessment || {
            passingScore: 70,
            maxAttempts: 3,
            timeLimit: 120
          },
          certificate: pathData.certificate || {
            enabled: true,
            template: 'default',
            validityPeriod: 24,
            requirements: {
              completionPercentage: 100,
              passingScore: 70
            }
          },
          pricing: pathData.pricing || {
            type: 'free',
            amount: 0,
            currency: 'USD'
          },
          settings: pathData.settings || {
            allowSelfEnrollment: true,
            requireApproval: false,
            allowPause: true,
            maxPauseDays: 30,
            allowRetake: true,
            retakeCooldown: 7
          }
        });
        setShowEditModal(true);
        setValidationErrors({});

        // Load available courses for the edit modal
        await loadAvailableCourses();
      } else {
        setError('Failed to fetch learning path details');
      }
    } catch (error) {
      setError('Failed to fetch learning path details');
    } finally {
      setLoading(false);
    }
  };

  // Handle update learning path
  const handleUpdatePath = async () => {
    if (!selectedPath) return;

    // Validate form before submission
    const formErrors = validatePathForm(pathForm);

    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);
      setTouched({
        title: true,
        description: true,
        category: true,
        courses: true
      });
      setError(null); // Clear general error, show field-specific errors

      // Auto-scroll to first validation error
      setTimeout(() => {
        const firstErrorField = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }, 100);

      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Calculate estimatedDuration from courses (in hours)
      const coursesForPayload = pathForm.courses?.map(course => ({
        course: course.id,
        order: pathForm.courses.indexOf(course) + 1,
        isRequired: course.isRequired || true
      })) || [];

      // Calculate total duration from courses in minutes, then convert to hours
      const totalMinutes = pathForm.courses?.reduce((total, c) => total + (Number(c.duration) || 0), 0) || 0;
      const calculatedEstimatedDuration = Math.round(totalMinutes / 60) || 0;

      // Prepare the learning path data with same structure as create
      const learningPathData = {
        title: pathForm.title,
        description: pathForm.description,
        category: pathForm.category,
        estimatedDuration: calculatedEstimatedDuration,
        status: pathForm.status || 'draft',
        completionCriteria: pathForm.completionCriteria,
        enrollment: {
          maxStudents: pathForm.enrollment?.maxStudents || 100,
          autoEnrollment: pathForm.enrollment?.autoEnrollment || false,
          enrollmentDeadline: pathForm.enrollmentDeadline
        },
        courses: coursesForPayload,
        learningObjectives: pathForm.learningObjectives?.filter(obj => obj.trim()) || [],
        skills: pathForm.skills?.filter(skill => skill.trim()) || [],
        tags: pathForm.tags || []
      };

      const result = await trainingService.updateLearningPath(selectedPath.id, learningPathData);

      if (result.success) {
        setShowEditModal(false);
        setSelectedPath(null);
        loadLearningPaths(); // Reload the list
      } else {
        setError(result.error || 'Failed to update learning path');
      }
    } catch (error) {
      setError('Failed to update learning path. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  // Handle publish learning path
  const handlePublishPath = async (pathId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await trainingService.publishLearningPath(pathId);

      if (result.success) {
        loadLearningPaths(); // Reload the list
        alert('Learning path published successfully!');
      } else {
        setError(result.error || 'Failed to publish learning path');
      }
    } catch (error) {
      setError('Failed to publish learning path. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle archive learning path
  const handleArchivePath = async (pathId) => {
    if (!window.confirm('Are you sure you want to archive this learning path?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await trainingService.archiveLearningPath(pathId);

      if (result.success) {
        loadLearningPaths(); // Reload the list
        alert('Learning path archived successfully!');
      } else {
        setError(result.error || 'Failed to archive learning path');
      }
    } catch (error) {
      setError('Failed to archive learning path. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle view learning path – navigate to dedicated full-page detail
  const handleViewPath = (pathId) => {
    navigate(`/hrms/training/learning-path/${pathId}`);
  };

  const handleClosePathDetails = () => {
    setShowPathDetails(false);
    setSelectedPathForView(null);
    setPathDetailsError(null);
  };

  // Handle preview learning path
  const handlePreviewPath = () => {
    if (pathForm.title && typeof pathForm.title === 'string' && pathForm.title.trim() !== '') {
      setShowPreviewModal(true);
    } else {
      alert('Please fill in the learning path title before previewing.');
    }
  };

  // Handle course selection
  const handleToggleCourse = (course) => {
    const updatedCourses = [...(pathForm.courses || [])];
    const existingIndex = updatedCourses.findIndex(c => c.id === course.id);

    if (existingIndex >= 0) {
      // Remove course
      updatedCourses.splice(existingIndex, 1);
    } else {
      // Add course
      updatedCourses.push({
        id: course.id,
        title: course.title,
        level: course.level,
        duration: course.duration,
        category: course.category,
        isRequired: true
      });
    }

    const totalDuration = updatedCourses.reduce((total, c) => total + (c.duration || 0), 0);

    setPathForm(prev => ({
      ...prev,
      courses: updatedCourses,
      estimatedDuration: Math.round(totalDuration / 60)
    }));
  };

  // Handle remove course from sequence
  const handleRemoveCourse = (courseId) => {
    const updatedCourses = pathForm.courses?.filter(c => c.id !== courseId) || [];
    setPathForm(prev => ({
      ...prev,
      courses: updatedCourses,
      estimatedDuration: Math.round(updatedCourses.reduce((total, c) => total + c.duration, 0) / 60)
    }));
  };


  // Handle delete learning path
  const handleDeletePath = (pathId, pathTitle) => {
    setDeleteConfirmation({ show: true, pathId, pathTitle });
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    const { pathId } = deleteConfirmation;

    setLoading(true);
    setError(null);
    setDeleteConfirmation({ show: false, pathId: null, pathTitle: '' });

    try {
      const result = await trainingService.deleteLearningPath(pathId);

      if (result.success) {
        loadLearningPaths(); // Reload the list
      } else {
        setError(result.error || 'Failed to delete learning path');
      }
    } catch (error) {
      setError('Failed to delete learning path. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteConfirmation({ show: false, pathId: null, pathTitle: '' });
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get difficulty badge color
  const getDifficultyBadgeColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-orange-100 text-orange-800';
      case 'expert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to capitalize values for display

  // Helper function to capitalize values for display
  const capitalizeValue = (value) => {
    if (!value) return '';

    // Handle special cases
    const specialCases = {
      'soft_skills': 'Soft Skills',
      'all_employees': 'All Employees',
      'super_admin': 'Super Admin',
      'hr_manager': 'HR Manager',
      'hr_executive': 'HR Executive',
      'sales_manager': 'Sales Manager',
      'sales_executive': 'Sales Executive',
      'business_relationship_manager': 'Business Relationship Manager',
      'business_development_executive': 'Business Development Executive',
      'dispatch_manager': 'Dispatch Manager',
      'support_manager': 'Support Manager'
    };

    if (specialCases[value]) {
      return specialCases[value];
    }

    // Default capitalization
    return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Learning Paths</h1>
          <p className="text-gray-600 mt-1">Create structured learning sequences with courses and prerequisites</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="training" action="delete">
              <button
                onClick={handleBulkDelete}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedRows.size})
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="training" action="read">
            <div className="relative">
              <Button
                variant="outline"
                icon={exportLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ""}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showInlineSelectedFormats ? "rotate-180" : ""}`} />
              </Button>

              <SelectedExportFormatBar
                isOpen={showInlineSelectedFormats}
                onClose={() => setShowInlineSelectedFormats(false)}
                onExport={handleExportSelected}
                loading={exportLoading}
                className="absolute right-0 mt-2 z-50 min-w-[200px]"
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="training" action="create">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Learning Path
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by Title..."
              value={searchTerm}
              onChange={handleSearch}
              onKeyPress={handleSearchKeyPress}
              maxLength={100}
              className="w-full h-10 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  const input = document.querySelector('input[placeholder="Search learning paths..."]');
                  if (input) input.focus();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex-shrink-0">
            <select
              value={selectedFilters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Categories</option>
              {pathCategories.map((c, index) => (
                <option key={c._id || c.id || `path-cat-${index}`} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="flex-shrink-0">
            <select
              value={selectedFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={handleDateChange}
            />
          </div>

          {(searchTerm || selectedFilters.category !== 'all' || selectedFilters.status !== 'all' || filterStartDate || filterEndDate) && (
            <div className="relative flex-shrink-0">
              <PermissionGate permission="training" action="read">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="h-10"
                >
                  Clear
                </Button>
              </PermissionGate>
            </div>
          )}
        </div>
      </div>

      {/* Learning Paths List */}
      {learningPaths && learningPaths.length > 0 && (
        <>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <colgroup>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                        checked={learningPaths?.length > 0 && learningPaths.every(path => selectedRows.has(path.id))}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Title</span>
                        {sortBy === 'title' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('description')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Description</span>
                        {sortBy === 'description' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('category')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Category</span>
                        {sortBy === 'category' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortBy === 'status' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('courses')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Courses</span>
                        {sortBy === 'courses' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('duration')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Duration</span>
                        {sortBy === 'duration' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created</span>
                        {sortBy === 'createdAt' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {sortedPaths.map((path, index) => (
                    <tr key={path._id || path.id || `path-row-${index}`} className={`hover:bg-gray-50 transition-colors ${selectedRows.has(path.id) ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                          checked={selectedRows.has(path.id)}
                          onChange={() => handleSelectRow(path)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900 capitalize max-w-xs truncate">
                          {path.title}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700 max-w-xs truncate">
                          {path.shortDescription || path.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {capitalizeValue(path.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadgeColor(path.status)}`}>
                          {capitalizeValue(path.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {path.totalCourses || path.courses?.length || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {formatDuration(getPathDurationInMinutes(path))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {path.createdAt ? formatDate(path.createdAt, { includeTime: true }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <PermissionGate permission="training" action="read">
                            <button
                              onClick={() => handleViewPath(path.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="training" action="update">
                            <button
                              onClick={() => handleEditPath(path)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="training" action="delete">
                            <button
                              onClick={() => handleDeletePath(path.id, path.title)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Learning Paths"
        scopedFormatExport={{
          onExport: handleExportScoped,
          showDateRange: true,
        }}
      />

      {/* Empty State */}
      {learningPaths && learningPaths.length === 0 && !loading && (
        <div className="text-center py-12">
          <BookMarked className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No learning paths found</h3>
          <p className="text-gray-600 mb-6">Get started by creating your first learning path</p>
          <PermissionGate permission="training" action="create">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors mx-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Learning Path
            </button>
          </PermissionGate>
        </div>
      )}

      {/* Pagination */}
      {shouldShowPagination && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Showing {paginationRangeStart} to {paginationRangeEnd} of {totalPaths || 0} results
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Items per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <PermissionGate permission="training" action="read">
                <button
                  onClick={() => handlePageChange(currentPageNum - 1)}
                  disabled={!hasPrevPage}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
              </PermissionGate>
              <div className="flex items-center space-x-1">
                {(() => {
                  const pages = [];
                  if (totalPages <= 10) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (currentPageNum <= 4) {
                      for (let i = 2; i <= 5; i++) {
                        pages.push(i);
                      }
                      pages.push('ellipsis');
                      pages.push(totalPages);
                    } else if (currentPageNum >= totalPages - 3) {
                      pages.push('ellipsis');
                      for (let i = totalPages - 4; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push('ellipsis');
                      for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                        pages.push(i);
                      }
                      pages.push('ellipsis');
                      pages.push(totalPages);
                    }
                  }

                  return pages.map((page, index) => {
                    if (page === 'ellipsis') {
                      return (
                        <span key={`lp-ellipsis-${index}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }

                    return (
                      <PermissionGate permission="training" action="read" key={`lp-page-${page}`}>
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                            ? 'bg-agrosaf-green text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      </PermissionGate>
                    );
                  });
                })()}
              </div>
              <PermissionGate permission="training" action="read">
                <button
                  onClick={() => handlePageChange(currentPageNum + 1)}
                  disabled={!hasNextPage}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}


      {/* Create Learning Path Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create Learning Path</h2>
                <PermissionGate permission="training" action="read">
                  <button
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(false);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </PermissionGate>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Basic Information & Completion Policy */}
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Path Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="title"
                          data-field="title"
                          value={pathForm.title}
                          maxLength={100}
                          required
                          onKeyPress={handleTextKeyPress}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Filter out invalid characters (alphanumeric, spaces, commas only)
                            value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                            // Enforce max length
                            if (value.length > 100) {
                              value = value.substring(0, 100);
                            }
                            // Capitalize first letter
                            value = capitalizeFirstLetter(value);
                            setPathForm(prev => ({ ...prev, title: value }));

                            // Real-time validation for learning path title
                            // We validate immediately on change, regardless of touched state
                            // UI will handle showing errors only if touched or on submit
                            const tempForm = { ...pathForm, title: value };
                            const errors = validatePathForm(tempForm);

                            setValidationErrors(prev => ({
                              ...prev,
                              title: errors.title || null
                            }));
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.title && validationErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          placeholder="Enter learning path title"
                        />
                        <div className="flex justify-between items-start mt-1">
                          <div className="flex-1">
                            {touched.title && validationErrors.title && (
                              <p className="text-sm text-red-600 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {validationErrors.title}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {pathForm.title?.length || 0}/100
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="description"
                          data-field="description"
                          value={pathForm.description}
                          maxLength={500}
                          onKeyPress={handleTextKeyPress}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Filter out invalid characters (alphanumeric, spaces, commas only)
                            value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                            // Enforce max length
                            if (value.length > 500) {
                              value = value.substring(0, 500);
                            }
                            setPathForm(prev => ({ ...prev, description: value }));

                            // Real-time validation for description
                            const tempForm = { ...pathForm, description: value };
                            const errors = validatePathForm(tempForm);

                            setValidationErrors(prev => ({
                              ...prev,
                              description: errors.description || null
                            }));
                          }}
                          rows={4}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent resize-none ${touched.description && validationErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          placeholder="Enter learning path description"
                        />
                        <div className="flex justify-between items-start mt-1">
                          <div className="flex-1">
                            {touched.description && validationErrors.description && (
                              <p className="text-sm text-red-600 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {validationErrors.description}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {pathForm.description?.length || 0}/500
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="category"
                          data-field="category"
                          value={pathForm.category}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPathForm(prev => ({ ...prev, category: value }));

                            // Real-time validation for category
                            const tempForm = { ...pathForm, category: value };
                            const errors = validatePathForm(tempForm);

                            setValidationErrors(prev => ({
                              ...prev,
                              category: errors.category || null
                            }));
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.category && validationErrors.category ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                        >
                          <option value="">Select Category</option>
                          {pathCategories.map((c, index) => (
                            <option key={c._id || c.id || `create-path-cat-${index}`} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                        {touched.category && validationErrors.category && (
                          <p className="text-red-600 text-sm mt-1 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.category}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          name="status"
                          data-field="status"
                          value={pathForm.status || 'draft'}
                          onChange={(e) => {
                            setPathForm(prev => ({ ...prev, status: e.target.value }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>


                    </div>
                  </div>
                </div>

                {/* Right Column - Course Sequence & Summary */}
                <div className="space-y-6">
                  {/* Course Sequence */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Course Sequence</h3>
                      <button
                        type="button"
                        onClick={() => {
                          loadAvailableCourses();
                          setCourseSearchTerm('');
                          setDebouncedCourseSearchTerm('');
                          setCourseCategoryFilter('all');
                          setShowCourseModal(true);
                        }}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Courses
                      </button>
                    </div>

                    <div className="space-y-3">
                      {pathForm.courses?.map((course, index) => (
                        <div key={course._id || course.id || `create-course-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                              <div className="w-4 h-4 cursor-move">
                                <div className="w-full h-full flex flex-col space-y-1">
                                  <div className="w-full h-0.5 bg-gray-400"></div>
                                  <div className="w-full h-0.5 bg-gray-400"></div>
                                  <div className="w-full h-0.5 bg-gray-400"></div>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{course.title}</div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${course.level === 'beginner' ? 'bg-green-100 text-green-800' :
                                  course.level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                  {course.level}
                                </span>
                                <span className="text-sm text-gray-600">{formatDuration(Number(course.duration))}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleRemoveCourse(course.id)}
                              className="p-1 text-red-400 hover:text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {(!pathForm.courses || pathForm.courses.length === 0) && (
                        <div className="text-center py-8 text-gray-500">
                          <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>No courses added yet</p>
                          <p className="text-sm">Click "Add Courses" to get started</p>
                          {validationErrors.courses && (
                            <p className="text-red-600 text-sm mt-2">{validationErrors.courses}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Course Summary */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Course Summary</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Courses</div>
                        <div className="text-lg font-semibold text-gray-900">{pathForm.courses?.length || 0}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Duration</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {(() => {
                            const totalMinutes = pathForm.courses?.reduce((total, c) => total + (Number(c.duration) || 0), 0) || 0;
                            return formatDuration(totalMinutes);
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <PermissionGate permission="training" action="read">
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(false);
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </PermissionGate>
                <PermissionGate permission="training" action="create">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCreatePath(e);
                    }}
                    disabled={loading}
                    className="px-6 py-2 bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Create Path
                      </>
                    )}
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Learning Path Modal - Reuse Create Modal */}
      {showEditModal && selectedPath && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[95vh] overflow-hidden flex flex-col">
            <div className="p-6 overflow-y-auto flex-1">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Edit Learning Path</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPath(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Basic Information & Completion Policy */}
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Path Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={pathForm.title}
                          maxLength={100}
                          required
                          onKeyPress={handleTextKeyPress}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Filter out invalid characters (alphanumeric, spaces, commas only)
                            value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                            // Enforce max length
                            if (value.length > 100) {
                              value = value.substring(0, 100);
                            }
                            // Capitalize first letter
                            value = capitalizeFirstLetter(value);
                            setPathForm(prev => ({ ...prev, title: value }));

                            // Real-time validation for learning path title
                            // We validate immediately on change, regardless of touched state
                            const tempForm = { ...pathForm, title: value };
                            const errors = validatePathForm(tempForm);

                            setValidationErrors(prev => ({
                              ...prev,
                              title: errors.title || null
                            }));
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.title && validationErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          placeholder="Enter learning path title"
                          data-field="title"
                        />
                        <div className="flex justify-between items-start mt-1">
                          <div className="flex-1">
                            {touched.title && validationErrors.title && (
                              <p className="text-sm text-red-600 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {validationErrors.title}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {pathForm.title?.length || 0}/100
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={pathForm.description}
                          maxLength={500}
                          required
                          onKeyPress={handleTextKeyPress}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Filter out invalid characters (alphanumeric, spaces, commas only)
                            value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                            // Enforce max length
                            if (value.length > 500) {
                              value = value.substring(0, 500);
                            }
                            setPathForm(prev => ({ ...prev, description: value }));

                            // Real-time validation for description
                            const tempForm = { ...pathForm, description: value };
                            const errors = validatePathForm(tempForm);

                            setValidationErrors(prev => ({
                              ...prev,
                              description: errors.description || null
                            }));
                          }}
                          rows={3}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent resize-none ${touched.description && validationErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          placeholder="Enter learning path description"
                          data-field="description"
                        />
                        <div className="flex justify-between items-start mt-1">
                          <div className="flex-1">
                            {touched.description && validationErrors.description && (
                              <p className="text-sm text-red-600 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {validationErrors.description}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {pathForm.description?.length || 0}/500
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={pathForm.category}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPathForm(prev => ({ ...prev, category: value }));

                            // Real-time validation for category
                            const tempForm = { ...pathForm, category: value };
                            const errors = validatePathForm(tempForm);

                            setValidationErrors(prev => ({
                              ...prev,
                              category: errors.category || null
                            }));
                          }}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.category && validationErrors.category ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          data-field="category"
                        >
                          <option value="">Select Category</option>
                          {pathCategories.map((c, index) => (
                            <option key={c._id || c.id || `edit-path-cat-${index}`} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                        {touched.category && validationErrors.category && (
                          <p className="text-red-600 text-sm mt-1">{validationErrors.category}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          value={pathForm.status}
                          onChange={(e) => setPathForm(prev => ({ ...prev, status: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>

                    </div>
                  </div>

                </div>

                {/* Right Column - Course Sequence & Course Summary */}
                <div className="space-y-6">
                  {/* Course Sequence */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Course Sequence</h3>
                      <button
                        onClick={() => {
                          loadAvailableCourses();
                          setCourseSearchTerm('');
                          setDebouncedCourseSearchTerm('');
                          setCourseCategoryFilter('all');
                          setShowCourseModal(true);
                        }}
                        className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Courses
                      </button>
                    </div>

                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {pathForm.courses?.length > 0 ? (
                        pathForm.courses?.map((course, index) => (
                          <div key={course._id || course.id || `edit-course-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2">
                                <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                                <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{course.title}</div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${course.level === 'beginner' ? 'bg-green-100 text-green-800' :
                                    course.level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                    {course.level}
                                  </span>
                                  <span className="text-sm text-gray-600">{formatDuration(Number(course.duration))}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleRemoveCourse(course.id)}
                                className="p-1 text-red-400 hover:text-red-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <BookOpen className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <p>No courses added yet</p>
                          <p className="text-sm">Click "Add Courses" to get started</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Course Summary */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Course Summary</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Courses</div>
                        <div className="text-lg font-semibold text-gray-900">{pathForm.courses?.length || 0}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Duration</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {(() => {
                            const totalMinutes = pathForm.courses?.reduce((total, c) => total + (Number(c.duration) || 0), 0) || 0;
                            return formatDuration(totalMinutes);
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - Fixed at bottom */}
            <div className="flex items-center justify-end space-x-3 p-6 pt-4 pb-6 border-t border-gray-200 bg-white">
              <PermissionGate permission="training" action="read">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPath(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </PermissionGate>
              <PermissionGate permission="training" action="update">
                <button
                  onClick={handleUpdatePath}
                  disabled={loading}
                  className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Path'}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Select Courses Modal */}
      {showCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Select Courses</h2>
                <button
                  onClick={() => setShowCourseModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Search and Filter */}
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={courseSearchTerm}
                    onChange={handleCourseSearch}
                    onKeyPress={(e) => {
                      // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                      if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength={100}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  />
                </div>
                <select
                  value={courseCategoryFilter}
                  onChange={handleCourseCategoryFilter}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {courseCategories.map((c, index) => (
                    <option key={c._id || c.id || `course-cat-${index}`} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Course List */}
              <div className="max-h-96 overflow-y-auto space-y-3">
                {getFilteredCourses().length > 0 ? (
                  getFilteredCourses().map((course, index) => {
                    const isSelected = pathForm.courses?.some(c => c.id === course.id) || false;
                    return (
                      <div
                        key={course._id || course.id || `filter-course-${index}`}
                        onClick={() => handleToggleCourse(course)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${isSelected
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleCourse(course)}
                              className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded pointer-events-none"
                            />
                            <div>
                              <div className="font-medium text-gray-900">{course.title}</div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {typeof course.category === 'object' ? course.category?.name : course.category || 'Uncategorized'}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${course.level === 'beginner' ? 'bg-green-100 text-green-800' :
                                  course.level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                  {course.level}
                                </span>
                                <span className="text-sm text-gray-600">
                                  {formatDuration(Number(course.duration))}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <BookOpen className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                    <h3 className="text-sm font-medium text-gray-900">No courses found</h3>
                    <p className="text-xs text-gray-500 mt-1">Try adjusting your search or category filter</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {(() => {
                    const totalMinutes = pathForm.courses?.reduce(
                      (total, c) => total + (Number(c.duration) || 0),
                      0
                    ) || 0;

                    return `${pathForm.courses?.length || 0} courses • ${formatDuration(totalMinutes)}`;
                  })()}
                </div>
                <button
                  onClick={() => setShowCourseModal(false)}
                  className="px-6 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Deletion</h3>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-red-600">{selectedRows.size}</span>{' '}
              selected learning path{selectedRows.size !== 1 ? 's' : ''}?
            </p>
            <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isBulkDeleting ? (
                  <>
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete {selectedRows.size} Path{selectedRows.size !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the learning path? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <PermissionGate permission="training" action="read">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </PermissionGate>
              <PermissionGate permission="training" action="delete">
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningPaths;